/**
 * The onboarding wizard's progress for the views (phase 7b item 3): read, and
 * one step or status recorded at a time.
 *
 * **On a node** there is no onboarding route (the progress is the cloud's):
 * the read answers `not-found`, which becomes `persisted: false` — the wizard
 * still works from the tenant's own lists (a show style exists…) and simply
 * does not remember a skip, and never opens by itself.
 */
import { ApiError, type ApiClient } from '@acp/api-client/client.js';
import type { Onboarding, UpdateOnboardingRequest } from '@acp/api-contract/onboarding.js';
import { seedPatches, STUDIO_SHOW, type SeedWriter } from '@acp/engine/seedShow.js';
import { type QueryClient, queryOptions } from '@tanstack/react-query';

export const onboardingKeys = {
  all: ['onboarding'] as const,
  one: () => [...onboardingKeys.all, 'one'] as const,
};

export type OnboardingRead = { readonly persisted: true; readonly onboarding: Onboarding } | { readonly persisted: false };

export function onboardingQuery(api: ApiClient) {
  return queryOptions({
    queryKey: onboardingKeys.one(),
    queryFn: async (): Promise<OnboardingRead> => {
      try {
        return { persisted: true, onboarding: (await api.call('getOnboarding', { params: null, query: null, body: null })).onboarding };
      } catch (err) {
        // A node (no such route), or a caller who is not a tenant admin: nothing to remember progress in.
        if (err instanceof ApiError && (err.code === 'not-found' || err.code === 'forbidden')) return { persisted: false };
        throw err;
      }
    },
  });
}

/** A site made for step 3 and a fresh enrolment code for it: the install line to show. */
export type SiteEnrolment = { readonly siteId: string; readonly siteName: string; readonly dockerRun: string; readonly expiresAt: string };

/** How long step 3's enrolment code lives (the contract allows up to 24 h). */
export const ENROLMENT_CODE_MINUTES = 24 * 60;

/**
 * Step 3: a site (idempotent by the client-chosen `siteId` — a retry answers the same site) and a fresh one-time
 * code for it. A code is shown once and can only be issued again, so a retry issues another; the old one expires.
 */
export function createSiteWithCodeMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (input: { readonly siteId: string; readonly name: string }): Promise<SiteEnrolment> => {
      const site = (await api.call('createSite', { params: null, query: null, body: { siteId: input.siteId, name: input.name } })).site;
      const code = (await api.call('createEnrolmentCode', { params: { siteId: site.siteId }, query: null, body: { expiresInMinutes: ENROLMENT_CODE_MINUTES } })).enrolment;
      return { siteId: site.siteId, siteName: site.name, dockerRun: code.dockerRun, expiresAt: code.expiresAt };
    },
    onSuccess: async (): Promise<void> => {
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  };
}

/** What seeding a rundown needs of the session's writer (`lib/editor/writer.ts`). */
export type SeedWriterSource = () => SeedWriter;

/** The template's demo show into an empty rundown, segments then parts then pieces; one with rows already is left alone. */
async function seedDemoShow(api: ApiClient, rundownId: string, writer: SeedWriterSource): Promise<void> {
  const existing = await api.call('listRows', { params: { rundownId }, query: { parentId: rundownId, limit: 1 }, body: null });
  if (existing.rows.length > 0) return;
  const batches = seedPatches(STUDIO_SHOW, rundownId, writer());
  for (const patches of [batches.segments, batches.parts, batches.pieces]) {
    const res = await api.call('patchRows', { params: { rundownId }, query: null, body: { patches } });
    const refused = res.results.find((r) => r.status === 'nack');
    if (refused !== undefined) throw new Error(`seeding the rundown stopped: the server refused a row (${JSON.stringify(refused).slice(0, 300)})`);
  }
}

/**
 * Step 4: a rundown, idempotent by its client-minted id, and — when `seed` — the template's demo show
 * (`@acp/engine/seedShow.js`' `STUDIO_SHOW`, the pieces the template show style and studio play) written as
 * ordinary patches. A rundown that already has segments (a retry after the seed went through) is not seeded again.
 */
export function createFirstRundownMutation(api: ApiClient, queryClient: QueryClient, writer: SeedWriterSource) {
  return {
    mutationFn: async (input: { readonly rundownId: string; readonly name: string; readonly seed: boolean }): Promise<{ readonly rundownId: string; readonly name: string }> => {
      const created = (await api.call('createRundown', { params: null, query: null, body: { rundownId: input.rundownId, name: input.name, playlistId: null, ord: null } })).rundown;
      if (input.seed) await seedDemoShow(api, created.rowId, writer);
      return { rundownId: created.rowId, name: input.name };
    },
    onSuccess: async (): Promise<void> => {
      await queryClient.invalidateQueries({ queryKey: ['rundowns'] });
    },
  };
}

export function updateOnboardingMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (input: UpdateOnboardingRequest): Promise<OnboardingRead> => {
      try {
        return { persisted: true, onboarding: (await api.call('updateOnboarding', { params: null, query: null, body: input })).onboarding };
      } catch (err) {
        if (err instanceof ApiError && (err.code === 'not-found' || err.code === 'forbidden')) return { persisted: false };
        throw err;
      }
    },
    onSuccess: (read: OnboardingRead): void => {
      if (read.persisted) queryClient.setQueryData(onboardingKeys.one(), read);
    },
  };
}
